/**
 * This file contains keys and paths to fields that
 * are used in Next. These can be used to "inject"
 * certain behavior like validations.
 */

/**
 * Keys of known fieldsets for Next.
 */
export enum NextFieldsetKey {
  Personal = 'personalDataKeys',
  ContactInfo = 'contactDataKeys',
  Contribution = 'contributionData',
  Sponsorships = 'sponsorships',
  // Fieldsets for bank data
  BankDataSepa = 'sepaBankData',
  BankDataSepaEur = 'sepaEurBankData',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  BankDataDirectDebitCH = 'swissDirectDebit',
  BankDataPostFinance = 'postfinanceBankData',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  BankDataSortCodeUK = 'sortCodesBankData',
  Signature = 'signature',
}

/**
 * Keys of known fields for Next.
 */
export enum NextFieldKey {
  // Bank data fields
  BankIbanOrNumber = 'account_iban_or_number',
  BankBicOrBankCode = 'account_bic_or_bankcode',
  BankName = 'account_bank_name',
  // Contact Data
  ContactDataOptIn = 'contact_data_conf',
  Email = 'email',
  PhoneNumber = 'phone_number',
  MobileNumber = 'mobile_number',
  // Contribution data
  Currency = 'currency',
  DonationAmount = 'donation_amount',
  DonationAmountPerInterval = 'donation_amount_per_interval',
  Interval = 'direct_debit_interval',
  PaymentMethod = 'payment_method',
  // Personal data
  CorrespondenceLanguage = 'correspondence_language',
  // Sponsorships
  Sponsorship = 'sponsorship',
  // Signature
  UseSelfCheckout = 'self_checkout', // sign on tablet or supporter device
}

/**
 * Describes the path to a certain field
 */
type NextFieldPath<FS extends NextFieldsetKey, F extends NextFieldKey> = [
  `${FS}`,
  `${F}`,
];

/**
 * @param fieldset Key of the fieldset
 * @param field Key of the field
 * @returns
 * A typed version of the path to the field
 */
function buildPath<FS extends NextFieldsetKey, F extends NextFieldKey>(
  fieldset: FS,
  field: F,
): NextFieldPath<FS, F> {
  return [`${fieldset}`, `${field}`];
}

/**
 * Paths of known fields in Next.
 */
export const nextFieldPath = Object.freeze({
  correspondenceLanguage: buildPath(
    NextFieldsetKey.Personal,
    NextFieldKey.CorrespondenceLanguage,
  ),
  contactDataOptIn: buildPath(
    NextFieldsetKey.ContactInfo,
    NextFieldKey.ContactDataOptIn,
  ),
  currency: buildPath(NextFieldsetKey.Contribution, NextFieldKey.Currency),
  donationAmount: buildPath(
    NextFieldsetKey.Contribution,
    NextFieldKey.DonationAmount,
  ),
  donationAmountPerInterval: buildPath(
    NextFieldsetKey.Contribution,
    NextFieldKey.DonationAmountPerInterval,
  ),
  directDebitInterval: buildPath(
    NextFieldsetKey.Contribution,
    NextFieldKey.Interval,
  ),
  paymentMethod: buildPath(
    NextFieldsetKey.Contribution,
    NextFieldKey.PaymentMethod,
  ),
  sponsorship: buildPath(
    NextFieldsetKey.Sponsorships,
    NextFieldKey.Sponsorship,
  ),
  useSelfCheckout: buildPath(
    NextFieldsetKey.Signature,
    NextFieldKey.UseSelfCheckout,
  ),
});

/**
 * Keys of fieldsets that contain an IBAN input field
 */
export const nextFieldsetsWithIban: readonly string[] = [
  NextFieldsetKey.BankDataSepa,
  NextFieldsetKey.BankDataSepaEur,
  NextFieldsetKey.BankDataDirectDebitCH,
  NextFieldsetKey.BankDataPostFinance,
];
